import { OktaAuth } from '@okta/okta-auth-js';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { PartialModes } from '../AltHome';
import DataSourceType from '../DataSourceType';
import GoalType from '../GoalType';
import Util from '../Util';
import { SalesforceApp, SalesforceDashboard, TriageCustomer, TriageProduct } from '../common/types';
import SFDCSourceFileType from '../sfdc/SFDCSourceFileType';
import configStore from '../stores/ConfigStore';

interface SFDCUserInfo {
    access_token: string;
    instance_url: string;
    organization_id?: string;
    refresh_token: string;
    user_email?: string;
    user_id?: string;
    user_name?: string;
}

interface SFDCOrgInfo {
    is_fsl: string;
    is_svmx: string;
    is_custom: string;
}

export interface SaveCredsResponse {
    source_auth_code?: string;
    source_domain?: string;
    target_auth_code?: string;
    target_domain?: string;
    source_user_info?: SFDCUserInfo;
    source_org_info?: SFDCOrgInfo;
}

interface SamplerReportColumn {
    column_name: string;
    total_missing_values: number;
}

interface SamplerReport {
    name: string;
    columns: Array<SamplerReportColumn>;
    total_lines: number;
    total_expected_values: number;
    total_missing_values: number;
}

interface ProcessResponse {
    id: string;
    message: string;
    wo_count: number;
    case_count: number;
    second_level_wo_count: number;
    template_id: string;
    report: Array<SamplerReport>;
}

interface UploadFileFieldMapping {
    target: string;
    source: string;
    score: number;
}

interface UploadFileInfo {
    file_name: string;
    field_mapping: Array<UploadFileFieldMapping>;
    report: Array<SamplerReport>;
}

interface UploadResponse {
    files: Array<UploadFileInfo>;
}

interface Environment {
    creds: string;
    resource_url: string;
    login_url: string;
    env_type: string;
    parent_config_id: string;
}

export interface Config {
    config_name: string;
    config_type: string;
    schedule_id: string;
    rules_id: string;
    environments: Array<Environment>;
    status: string;
    timeframe_from: Date;
    timeframe_to: Date;
    scop_project?: Boolean;
}

export interface ConfigsResponse {
    configs: Array<Config>;
    total: number;
}

export interface UserResponse {
    user_id: string;
    user_name: string;
}

export interface UserListResponse {
    users: Array<UserResponse>;
}

export interface UserManagementUserResponse {
    user_id: string;
    user_name: string;
    urole: string;
}

export interface UserManagementProjectResponse {
    config_id: string;
    config_name: string;
    cohort_types: Array<UserManagementCohortTypeResponse>;
}

export interface UserManagementCohortTypeResponse {
    cohort_id: string;
    cohort_name: string;
    cohort_label: string;
}

export interface UserManagementResponse {
    users: Array<UserManagementUserResponse>;
    projects: Array<UserManagementProjectResponse>;
}

export interface TaskDetail {
    rowid?: string;
    task_results?: string;
}

interface ConfigRunResponse {
    rowid: string;
}

interface DuplicateConfigResponse {
    rowid: string;
    task_config: string;
}

interface UpdateTemplateResponse {
    config_id: string;
    template: any;
    template_raw_json: string;
}

interface ScopSchema {
    rowid: String;
    reference: String;
    label: String;
    type: String;
    config_id: String;
}

interface UploadCsvResponse {
    task_id: string;
}

interface DeployReportResponse {
    task_id: string;
}

export interface NlpCustomerProjectInfo {
    nlp_name: string;
    nlp_id: number;
}

interface NlpCustomerInfo {
    nlp_customer_name: string;
    nlp_project_info: Array<NlpCustomerProjectInfo>;
}

export interface CreateConfigResponse {
    parent_config_id: string;
    data_processing_config_id: string;
    task_id?: string;
    environment_id: string;
}

export interface NlpInfo {
    nlp_customers: Array<NlpCustomerInfo>;
}

export interface sObjectRecordTypeInfo {
    developerName: string;
}

export interface sObjectFieldPicklistValue {
    value: string;
}

export interface sObjectField {
    label: string;
    api_name: string;
    type: string;
    relationshipName: string;
    referenceTo: Array<any>;
    picklistValues: Array<sObjectFieldPicklistValue>;
}

export interface sObject {
    label: string;
    api_name: string;
    fields: Array<sObjectField>;
    recordTypeInfos: Array<sObjectRecordTypeInfo>;
}

export interface ReportColumn {
    api_name: string;
    label: string;
    data_type: string;
}

export interface Report {
    name: string;
    id: string;
    columns: Array<ReportColumn>;
    filters_json: string;
    report_type: string;
}

export interface LookupFile {
    file_name: string;
    file_id: string;
    file_content: string;
    file_rowid: string;
}

export interface FileInfo {
    name: string;
    id: string;
    is_folder: string;
    last_modified_at: string;
    size: number;
}

export interface GetFolderListResponse {
    path: string;
    files: Array<FileInfo>;
}

export interface CreateFolderResponse {
    path: string;
    folder_name: string;
}

export interface DownloadStorageFileResponse {
    path: string;
    file_name: string;
}

export interface InjectInfo {
    app_name: string;
    instance_url: string;
    access_token: string;
}

export interface ProcessorInfo {
    with_process: boolean;
    with_inject: boolean;
    data?: any;
    inject_data?: any;
    ignore_errors?: boolean;
    process_to_db?: boolean;
}

export enum FileType {
    master = 'master',
    lookup = 'lookup',
    other = 'other',
}

// Scop Objects:

export interface ScopCohortType {
    id: string;
    name: string;
    label: string;
}

export interface ScopUser_new {
    user_id: string;
    user_name: string;
    config_id: string;
    config_name: string;
    cohort_type: UserManagementCohortTypeResponse;
    cohort_names?: string[];
}

export interface SetScopOff {
    config: any;
}

// Prefixes
const baseUrl = (window.REACT_APP_CONFIG?.REACT_APP_API_SERVER as string) || process.env.REACT_APP_API_SERVER || '';
const sfdcApiPrefix = '/sfdc/';
const csvApiPrefix = '/csv/';
const cdnApiPrefix = '/cdn/';

// URIs
const userInfoUrl = `${baseUrl}/userinfo`;
const getConfigsUrl = `${baseUrl}/configs_page`;
const getConfigUrl = `${baseUrl}/config`;
const getConfigStatusUrl = `${baseUrl}/config/status`;
const taskUrl = `${baseUrl}/task`;
const updateScop = `${baseUrl}/config/update_scop`;
const getScopSchemasUrl = `${baseUrl}/config/get_scop_schemas`;

const taskDetailUrl = `${taskUrl}/detail`;
const taskRevokeUrl = `${taskUrl}/revoke`;
const updateTaskFailedValidationUrl = `${taskUrl}/update_ignore_failed_validation`;
const nlpInfoUrl = `${baseUrl}/config/nlp/info`;
const configHistoryUrl = `${baseUrl}/config/history`;
const configRunUrl = `${baseUrl}/config/run`;
const runExistsProcessInjectUrl = `${baseUrl}/task/run_exists_process_inject`;
const configCreateUrl = `${baseUrl}/config/create`;
const updateConfigUrl = `${baseUrl}/config/update`;
const updateTemplateUrl = `${baseUrl}/config/template/update`;
const duplicateConfig = `${baseUrl}/config/duplicate`;
const configsNamesUrl = `${baseUrl}/config/names`;
const saveCredsUrl = `${baseUrl + sfdcApiPrefix}creds`;
const reconnectSFDCUrl = `${baseUrl + sfdcApiPrefix}reconnect`;
const saveRefreshTokenUrl = `${baseUrl + sfdcApiPrefix}save_refresh_token`;
const loginSFDCUrl = `${baseUrl + sfdcApiPrefix}login`;
const processUrl = `${baseUrl + sfdcApiPrefix}process`;
const uploadUrl = `${baseUrl + sfdcApiPrefix}upload-source/`;
const deployReportUrl = `${baseUrl + sfdcApiPrefix}report/deploy`;
const uploadReporCsvUrl = `${baseUrl + sfdcApiPrefix}report/csv`;
const getSfdcAppsUrl = `${baseUrl + sfdcApiPrefix}apps`;
const getSfdcAppDashboardsUrl = `${getSfdcAppsUrl}/dashboards`;
const getSfdcInstalledVerUrl = `${getSfdcAppsUrl}/getInstalledVersion`;
const getSfdcLatestVerUrl = `${getSfdcAppsUrl}/getLatestPackageVersion`;
const uploadSvmxUrl = `${uploadUrl}svmx`;
const uploadCsvUrl = `${baseUrl + csvApiPrefix}config/run`;
const uploadFromStorage = `${baseUrl + csvApiPrefix}config/storage/run`;
const getStorageUploadKeyUrl = `${baseUrl + csvApiPrefix}config/storage/create`;
const downloadResultsUrl = `${baseUrl + cdnApiPrefix}task/results`;
const getTriageCustomers = `${baseUrl + sfdcApiPrefix}triageCustomers`;
const getTriageProducts = `${baseUrl + sfdcApiPrefix}triageProducts`;
const downloadProcessorUrl = `${baseUrl + cdnApiPrefix}task/processor_output`;
const downloadUrl = `${baseUrl + cdnApiPrefix}download_output`;
const downloadMasterFileUrl = `${baseUrl + cdnApiPrefix}download_master_file`;

const sfExplorer = `${baseUrl + sfdcApiPrefix}explorer/`;
const listsfObjectsUrl = `${sfExplorer}sobjects`;
const sfObjectDetailsUrl = `${sfExplorer}sobject`;
const listsfOReportsUrl = `${sfExplorer}reports`;
const reportDetailUrl = `${sfExplorer}report`;
const runQueryUrl = `${sfExplorer}query`;

const lookupManager = `${baseUrl}/lm/`;
const listLookupFilesUrl = `${lookupManager}files`;
const createLookupFileUrl = `${lookupManager}file/create`;
const deleteLookupFileUrl = `${lookupManager}file/delete`;
const getFilesPresignedUrl = `${lookupManager}file/get_files_presigned_urls`;
const getLookupFileContent = `${lookupManager}file/get_content`;

const tenantsUrl = `${baseUrl}/tenants`;
const getAllTenantsUrl = `${tenantsUrl}/get_all`;
const tenantsSearchUrl = `${tenantsUrl}/search`;
const createTenantUrl = `${tenantsUrl}/create_tenant`;

const storageUrl = `${baseUrl}/storage`;
const listFolderUrl = `${storageUrl}/list`;
const folderUrl = `${storageUrl}/folder`;

const downloadStorageFileUrl = `${storageUrl}/download`;
const createFolderUrl = `${folderUrl}/create`;
const uploadFilesToStorageUrl = `${storageUrl}/upload`;
const bucket_name = 'si-tenants-ds01';

// scop request urls:
const setScopOff = `${baseUrl}/scop/set_scop_off`;

const usersAndProjectUrl = `${baseUrl}/scop/get_users_and_projects`;
const addScopUserUrl = `${baseUrl}/scop/add_scop_user`;
const getScopUsersUrl = `${baseUrl}/scop/get_connected_users`;
const deleteScopUserUrl = `${baseUrl}/scop/delete_scop_user`;
const updateScopUserConfigUrl = `${baseUrl}/scop/update_scop_project_for_user`;

const sfObjectDescribeCache = {};
const sfReportDescribeCache = {};

class Backend {
    private static getCommonHeaders(oktaAuth: OktaAuth) {
        return {
            Authorization: `Bearer ${oktaAuth.getAccessToken()}`,
            'X-Tenant': (Util.getSessionCurrentTenantInfo() && Util.getSessionCurrentTenantInfo().id
                ? Util.getSessionCurrentTenantInfo()
                : Util.getCurrentTenantInfo()
            ).id,
        };
    }

    public static async getUserInfo(oktaAuth: OktaAuth) {
        const req = {};
        return axios.post(userInfoUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getFilesPaths(configId: string, oktaAuth: OktaAuth) {
        const root_path = `user_uploads/${configId}`;
        const master_path = `${root_path}/master`;
        const lookup_path = `${root_path}/lookup`;
        const [mfiles, lfiles] = await Promise.all([
            Backend.listFolder(master_path, oktaAuth),
            Backend.listFolder(lookup_path, oktaAuth),
        ]);
        return [
            mfiles.data.files.map((file) => `${master_path}/${file.name}`),
            lfiles.data.files.map((file) => `${lookup_path}/${file.name}`),
        ];
    }

    public static async getFilesByFolderType(configId: string, type: string, oktaAuth: OktaAuth) {
        const root_path = `user_uploads/${configId}`;
        const files_path = `${root_path}/${type}`;
        const files_data = await Backend.listFolder(files_path, oktaAuth);
        return files_data.data.files;
    }

    public static async getConfigs(page: number, oktaAuth: OktaAuth, showTest: boolean = false) {
        const req = {
            page,
            show_test: showTest,
        };
        return axios.post<ConfigsResponse>(getConfigsUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async createTenant(tenantName: string, oktaAuth: OktaAuth) {
        const req = {
            tenant_name: tenantName,
        };

        try {
            return await axios.post(createTenantUrl, req, {
                headers: Backend.getCommonHeaders(oktaAuth),
            });
        } catch (e: any) {
            return e.response;
        }
    }

    public static async getConfig(oktaAuth: OktaAuth, configId?: String) {
        const paramUrl = configId ? `?config_id=${configId}` : '';
        return axios.get(getConfigUrl + paramUrl, {
            headers: {
                ...Backend.getCommonHeaders(oktaAuth),
            },
        });
    }

    public static async getConfigsNames(oktaAuth: OktaAuth, tenantId: string) {
        const paramUrl = `?tenant_id=${tenantId}`;
        return axios.get(configsNamesUrl + paramUrl, {
            headers: {
                ...Backend.getCommonHeaders(oktaAuth),
            },
        });
    }

    public static async getConfigStatus(oktaAuth: OktaAuth, configId?: String) {
        return axios.get(`${getConfigStatusUrl}?config_id=${configId}`, {
            headers: {
                ...Backend.getCommonHeaders(oktaAuth),
            },
        });
    }

    public static async saveCreds(
        source_auth_code: string,
        source_domain: string,
        target_auth_code: string,
        target_domain: string,
        oktaAuth: OktaAuth,
        source_code_verifier?: string,
        target_code_verifier?: string,
        redirect_uri?: string,
    ) {
        // TODO: need a context on the server to hold these together

        const req = {
            source_auth_code,
            source_domain,
            target_auth_code,
            target_domain,
            ...(source_code_verifier ? { source_code_verifier } : {}),
            ...(target_code_verifier ? { target_code_verifier } : {}),
            ...(redirect_uri ? { redirect_uri } : {}),
        };
        return axios.post<SaveCredsResponse>(saveCredsUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async reconnect(
        configId: string,
        authCode: string,
        domain: string,
        envType: 'Source' | 'Target',
        oktaAuth: OktaAuth,
        code_verifier?: string,
        redirect_uri?: string,
    ) {
        const req = {
            config_id: configId,
            auth_code: authCode,
            domain,
            env_type: envType,
            ...(code_verifier ? { code_verifier } : {}),
            ...(redirect_uri ? { redirect_uri } : {}),
        };
        return axios.post(reconnectSFDCUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async save_refresh_token(
        config_id: string,
        refresh_token: string,
        instance_url: string,
        env_type: 'Source' | 'Target',
        oktaAuth: OktaAuth,
    ) {
        const req = {
            config_id,
            refresh_token,
            instance_url,
            env_type,
        };
        return axios.post(saveRefreshTokenUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async login(
        config_id: string,
        ac: string,
        sd: string,
        env_type: 'Source' | 'Target',
        oktaAuth: OktaAuth,
        code_verifier?: string,
        redirect_uri?: string,
    ) {
        const req = {
            config_id,
            auth_code: ac,
            domain: sd,
            env_type,
            ...(code_verifier ? { code_verifier } : {}),
            ...(redirect_uri ? { redirect_uri } : {}),
        };
        return axios.post(loginSFDCUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async process(access_token: string, instance_url: string, org_info: SFDCOrgInfo, oktaAuth: OktaAuth) {
        // TODO: need a context on the server to hold these together

        const req = { access_token, instance_url, org_info };
        return axios.post<ProcessResponse>(processUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async uploadSvmx(files: any) {
        // TODO: need a context on the server to hold these together

        const formData = new FormData();
        formData.append('files', files[0]);
        formData.append('source_type', SFDCSourceFileType.svmx);
        return axios.post<UploadResponse>(uploadSvmxUrl, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    public static async getConfigHistory(config_id: string, oktaAuth: OktaAuth) {
        const req = { config_id };
        return axios.post(configHistoryUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getTaskDetail(task_id: string, oktaAuth: OktaAuth) {
        const req = { task_id };
        return axios.post<TaskDetail>(taskDetailUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async revokeTask(task_id: string, oktaAuth: OktaAuth) {
        const req = { task_id };
        return axios.post<TaskDetail>(taskRevokeUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async updateTaskIgnoreFailedValidation(
        task_id: string,
        ignore_failed_validation: boolean,
        oktaAuth: OktaAuth,
    ) {
        const req = { task_id, ignore_failed_validation };
        return axios.post(updateTaskFailedValidationUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getNlpInfo(req: any, oktaAuth: OktaAuth) {
        return axios.post<NlpInfo>(nlpInfoUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async runExistsProcessInject(task_id: string, oktaAuth: OktaAuth) {
        const req = { task_id };

        return axios.post<ConfigRunResponse>(runExistsProcessInjectUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async runConfig(
        config_id: string,
        oktaAuth: OktaAuth,
        startDate?: Date,
        endDate?: Date,
        process_info?: ProcessorInfo,
        isTest: boolean = false,
        processorOnly: boolean = false,
        isPartial: boolean = false,
        partialMode: PartialModes = null,
    ) {
        const req = {
            config_id,
            start_date: startDate && `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`,
            end_date: endDate && `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`,
            process_info,
            is_test: isTest,
            processor_only: processorOnly,
            is_partial: isPartial,
            partial_mode: partialMode,
        };

        configStore.processToDB = false;

        return axios.post<ConfigRunResponse>(configRunUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async duplicateProject(
        target_tenant_id: string,
        source_config_id: string,
        destination_config_name: string,
        oktaAuth: OktaAuth,
    ) {
        const req = {
            target_tenant_id,
            source_config_id,
            destination_config_name,
        };

        return axios.post<DuplicateConfigResponse>(duplicateConfig, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getScopSchemas(configId: string, oktaAuth: OktaAuth) {
        const req = { config_id: configId };
        const res = axios.post(getScopSchemasUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });

        return res;
    }

    public static async updateScop(configId: string, oktaAuth: OktaAuth) {
        const response = await axios
            .get<Boolean>(updateScop, {
                params: {
                    config_id: configId,
                },
                headers: {
                    ...Backend.getCommonHeaders(oktaAuth),
                },
            })
            .catch((error) => {
                console.error(
                    `Error with update scop to production. configId: ${configId} \n`,
                    error.response!.data!.detail,
                );
            });
        return response;
    }

    public static async updateTemplate(
        config_id: string,
        template: any,
        template_raw_json: string,
        update_description: string,
        oktaAuth: OktaAuth,
    ) {
        const req = { config_id, template, template_raw_json, update_description };
        let res = null;

        try {
            res = axios.post<UpdateTemplateResponse>(updateTemplateUrl, req, {
                headers: Backend.getCommonHeaders(oktaAuth),
            });
        } catch (e) {
            console.log(e);
        }

        return res;
    }

    public static async updateConfig(config, oktaAuth: OktaAuth) {
        let res = null;

        try {
            res = axios.post(updateConfigUrl, config, {
                headers: Backend.getCommonHeaders(oktaAuth),
            });
        } catch (e) {
            console.log(e);
        }

        return res;
    }

    public static async deployReport(sac: string, iu: string, st: DataSourceType, gt: GoalType, oktaAuth: OktaAuth) {
        const req = { access_token: sac, instance_url: iu, config_type: st, goal_type: gt };
        return axios.post<DeployReportResponse>(deployReportUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async doReportCsvUpload(files: any, deploy_task_id: string, oktaAuth: OktaAuth) {
        const formData = new FormData();
        formData.append('files', files[0]);
        formData.append('deploy_task_id', deploy_task_id);
        const headers = Backend.getCommonHeaders(oktaAuth) as {};
        (headers as any)['Content-Type'] = 'multipart/form-data';

        return axios.post<UploadResponse>(uploadReporCsvUrl, formData, {
            headers,
        });
    }

    public static async getOrgApps(injectInfo: InjectInfo, oktaAuth: OktaAuth) {
        return axios.post<SalesforceApp[]>(getSfdcAppsUrl, injectInfo, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getAppDashboards(injectInfo: InjectInfo, appId: string, oktaAuth: OktaAuth) {
        return axios.post<SalesforceDashboard[]>(`${getSfdcAppDashboardsUrl}?app_id=${appId}`, injectInfo, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getInstalledPackageVersion(injectInfo: InjectInfo, oktaAuth: OktaAuth) {
        return (
            await axios.post(getSfdcInstalledVerUrl, injectInfo, {
                headers: Backend.getCommonHeaders(oktaAuth),
            })
        ).data;
    }

    public static async getLatestPackageVersion(oktaAuth: OktaAuth) {
        return (
            await axios.get(getSfdcLatestVerUrl, {
                headers: Backend.getCommonHeaders(oktaAuth),
            })
        ).data;
    }

    private static async uploadFileToStorage(file: File, file_type: FileType, config_id: string, oktaAuth: OktaAuth) {
        // get presigned data
        const req = { config_id, file_name: file.name, file_type };
        const urlData = await axios.post(getStorageUploadKeyUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });

        // upload file
        const presignedData = urlData.data;
        const uploadCreds = presignedData.upload_creds;
        const uploadUrl = uploadCreds;
        const filePath = presignedData.file_name;

        await axios.put(uploadUrl, file, {
            headers: {
                'x-ms-blob-type': 'BlockBlob',
                'Content-Type': file.type,
            },
        });
        // error handling

        return filePath;
    }

    public static async doCsvFromStorage(
        master_files: any,
        lookup_files: any,
        config_id: string,
        process_info: ProcessorInfo,
        oktaAuth: OktaAuth,
        isTest: boolean = false,
        processorOnly: boolean = false,
        partialMode: PartialModes = null,
    ) {
        const req = {
            config_id,
            files: master_files,
            lookup: lookup_files,
            process_info,
            is_test: isTest,
            processor_only: processorOnly,
            partial_mode: partialMode,
        };

        return axios.post<UploadCsvResponse>(uploadFromStorage, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async saveFilesToStorage(files: any, files_type: FileType, config_id: string, oktaAuth: OktaAuth) {
        const awaitables = files.map((el) => Backend.uploadFileToStorage(el, files_type, config_id, oktaAuth));
        const paths = await Promise.all(awaitables);

        return paths.map((el) => ({ file_path: el }));
    }

    public static async doCsvUpload(
        master_files: any,
        lookup_files: any,
        config_id: string,
        process_info: ProcessorInfo,
        oktaAuth: OktaAuth,
        isTest: boolean = false,
        processorOnly: boolean = false,
        partialMode: PartialModes = null,
    ) {
        const [mfiles, lfiles] = await Promise.all([
            Backend.saveFilesToStorage(master_files, FileType.master, config_id, oktaAuth),
            Backend.saveFilesToStorage(lookup_files, FileType.lookup, config_id, oktaAuth),
        ]);

        return Backend.doCsvFromStorage(
            mfiles,
            lfiles,
            config_id,
            process_info,
            oktaAuth,
            isTest,
            processorOnly,
            partialMode,
        );
    }

    public static async downloadResultsViaUrl(results_id: string, oktaAuth: OktaAuth) {
        const response = await axios.get(`${downloadResultsUrl}?task_results_id=${results_id}`, {
            headers: {
                ...Backend.getCommonHeaders(oktaAuth),
            },
        });
        window.open(response.data, '_blank');
    }

    public static async downloadProcessorUrl(results_id: string, oktaAuth: OktaAuth) {
        const response = await axios.get(`${downloadProcessorUrl}?task_results_id=${results_id}`, {
            headers: {
                ...Backend.getCommonHeaders(oktaAuth),
            },
        });
        window.open(response.data, '_blank');
    }

    public static async downloadUrl(url: string, oktaAuth: OktaAuth) {
        const req = { url };
        const response = await axios.post(downloadUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
        window.open(response.data, '_blank');
    }

    public static async downloadMasterFileUrl(configId: string, oktaAuth: OktaAuth) {
        const response = await axios.get(`${downloadMasterFileUrl}?config_id=${configId}`, {
            headers: {
                ...Backend.getCommonHeaders(oktaAuth),
            },
        });

        window.open(response.data, '_blank');
    }

    public static async listSFObjects(
        config_id: string,
        access_token: string,
        oktaAuth: OktaAuth,
        resource_url?: string,
    ) {
        const req = { config_id, access_token, resource_url };
        return axios.post(listsfObjectsUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async listSFReports(
        config_id: string,
        search_text: string,
        access_token: string,
        oktaAuth: OktaAuth,
        resource_url?: string,
    ) {
        const req = { config_id, access_token, search_text, resource_url };
        return axios.post(listsfOReportsUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getSFObjectDetails(
        config_id: string,
        access_token: string,
        name: string,
        oktaAuth: OktaAuth,
        resource_url?: string,
    ) {
        const req = { config_id, access_token, name, resource_url };

        return new Promise<any>((resolve, reject) => {
            const id = `${name}_${config_id}`;
            const fromCache = sfObjectDescribeCache[id];
            if (fromCache) {
                resolve(fromCache);
            } else {
                axios
                    .post(sfObjectDetailsUrl, req, {
                        headers: Backend.getCommonHeaders(oktaAuth),
                    })
                    .then((response) => {
                        const r = response.data.details;
                        sfObjectDescribeCache[id] = r;
                        resolve(r);
                    });
            }
        });
    }

    public static async getReportDetails(
        config_id: string,
        access_token: string,
        report_id: string,
        oktaAuth: OktaAuth,
        resource_url?: string,
    ) {
        const req = { config_id, access_token, report_id, resource_url };

        return new Promise<any>((resolve, reject) => {
            const id = `${report_id}_${config_id}`;
            const fromCache = sfReportDescribeCache[id];
            if (fromCache) {
                resolve(fromCache);
            } else {
                axios
                    .post(reportDetailUrl, req, {
                        headers: Backend.getCommonHeaders(oktaAuth),
                    })
                    .then((response) => {
                        const r = response.data.details;
                        sfReportDescribeCache[id] = r;
                        resolve(r);
                    });
            }
        });
    }

    public static async listLookupFiles(oktaAuth: OktaAuth) {
        const req = {};
        return axios.post(listLookupFilesUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async deleteLookupFile(filesToDelete: LookupFile[], oktaAuth: OktaAuth) {
        const req = { files_to_delete: filesToDelete };
        return axios.post(deleteLookupFileUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async createLookupFile(file_id: string, file_name: string, file_content: string, oktaAuth: OktaAuth) {
        const req = { file_id, file_name, file_content };
        return axios.post(createLookupFileUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async downloadLookupfiles(filesNames: string[], oktaAuth: OktaAuth) {
        const req = { files_names: filesNames };
        const response = await axios.post(getFilesPresignedUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });

        response.data.forEach((file_url) => {
            window.open(file_url, '_blank');
        });
    }

    public static async getLookupFileContent(file_name: string, oktaAuth: OktaAuth) {
        const req = { file_name };
        return axios.post(getLookupFileContent, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async runSoqlQuery(
        config_id: string,
        access_token: string,
        query: string,
        oktaAuth: OktaAuth,
        resource_url?: string,
    ) {
        const req = { config_id, access_token, query, resource_url };
        return axios.post(runQueryUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    /**
     * this method returns the zip file synchronously. Because of the heroku router timeout limit (30 sec)
     * synchronous approach fails. For now leaving this code here
     */
    public static async downloadResults(results_id: string, oktaAuth: OktaAuth) {
        const response = await axios.get(`${downloadResultsUrl}?task_results_id=${results_id}`, {
            responseType: 'blob',
            headers: {
                ...Backend.getCommonHeaders(oktaAuth),
            },
        });

        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'results.zip';
        if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename=(.+)/);
            if (fileNameMatch && fileNameMatch.length === 2) {
                fileName = fileNameMatch[1];
            }
        }

        saveAs(response.data, fileName);
    }

    public static async createConfig(ccr: any, oktaAuth: OktaAuth) {
        return axios.post<CreateConfigResponse>(configCreateUrl, ccr, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getAllTenants(oktaAuth: OktaAuth) {
        const req = {};

        return axios.get(getAllTenantsUrl, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getTriageCustomers(oktaAuth: OktaAuth) {
        return axios.get<TriageCustomer[]>(getTriageCustomers, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async getTriageProducts(oktaAuth: OktaAuth) {
        return axios.get<TriageProduct[]>(getTriageProducts, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async searchTenants(searchValue: string, oktaAuth: OktaAuth) {
        const req = { search_text: searchValue };

        return axios.post(tenantsSearchUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async listFolder(path: string, oktaAuth: OktaAuth) {
        const req = { path };

        return axios.post<GetFolderListResponse>(listFolderUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async createFolder(path: string, folder_name: string, oktaAuth: OktaAuth) {
        const req = { path, folder_name };

        return axios.post<CreateFolderResponse>(createFolderUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async downloadStorageFile(path: string, file_name: string, oktaAuth: OktaAuth) {
        const req = { path, file_name };

        return axios.post<DownloadStorageFileResponse>(downloadStorageFileUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
    }

    public static async uploadFilesToStorage(files: any, path: string, oktaAuth: OktaAuth) {
        const formData = new FormData();

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            formData.append('files', file);
        }

        formData.append('path', path);

        const headers = Backend.getCommonHeaders(oktaAuth) as {};
        (headers as any)['Content-Type'] = 'multipart/form-data';

        return axios.post(uploadFilesToStorageUrl, formData, {
            headers,
        });
    }

    //Scop requests:
    public static async setScopOff(config_id: string, oktaAuth: OktaAuth) {
        const req = {
            config_id,
        };

        const res = axios.post<SetScopOff>(setScopOff, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });
        return res;
    }

    public static async getUsersAndProjects(oktaAuth: OktaAuth) {
        const response = await axios.post<UserManagementResponse>(
            usersAndProjectUrl,
            {},
            {
                headers: Backend.getCommonHeaders(oktaAuth),
            },
        );
        return response;
    }

    public static async addScopUser(userDetails: ScopUser_new, oktaAuth: OktaAuth) {
        const req = {
            user_id: userDetails.user_id,
            user_name: userDetails.user_name,
            config_id: userDetails.config_id,
            config_name: userDetails.config_name,
            cohort_type: userDetails.cohort_type ? userDetails.cohort_type.cohort_id : null,
            cohort_list: userDetails.cohort_names,
        };
        const response = await axios.post(addScopUserUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });

        return response;
    }

    public static async getScopUsers(oktaAuth: OktaAuth) {
        const response = await axios.post<ScopUser_new[]>(
            getScopUsersUrl,
            {},
            {
                headers: Backend.getCommonHeaders(oktaAuth),
            },
        );
        return response;
    }

    public static async deleteScopUser(oktaAuth: OktaAuth, userId: string, configId: string) {
        const req = {
            user_id: userId,
            config_id: configId,
        };
        const response = await axios.post<boolean>(deleteScopUserUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });

        return response;
    }

    public static async updateScopUserConfig(
        oktaAuth: OktaAuth,
        userId: string,
        oldConfigId: string,
        newConfigId: string,
        new_cohort_type: string,
        new_cohort_list: string[],
    ) {
        const req = {
            user_id: userId,
            old_config_id: oldConfigId,
            new_config_id: newConfigId,
            new_cohort_type,
            new_cohort_list,
        };
        const response = await axios.post<boolean>(updateScopUserConfigUrl, req, {
            headers: Backend.getCommonHeaders(oktaAuth),
        });

        return response;
    }
}

export default Backend;
export type UserInfo = SFDCUserInfo;
export type OrgInfo = SFDCOrgInfo;
export type UploadResponseInfo = UploadResponse;
